package com.islamicmedia.shared.util

expect fun currentTimeMillis(): Long

/**
 * Epoch millis from a Postgres `timestamptz` as PostgREST serialises it, or null if unparseable.
 *
 * Instant.parse alone is NOT enough here: PostgREST emits `2026-09-04 06:39:05.8909+00` — a SPACE
 * instead of the ISO `T`, and a two-digit offset rather than `Z` or `+00:00`. Both make Instant.parse
 * throw, so a caller that only tried the strict parse would silently treat every timestamp as
 * unreadable and fall back to whatever its error path does.
 */
fun parseTimestampMillis(raw: String): Long? {
    val s = raw.trim().replace(' ', 'T')
    val candidates = listOf(
        s,
        // "+00" / "-05" -> "+00:00" / "-05:00"
        if (Regex("""[+-]\d{2}$""").containsMatchIn(s)) "$s:00" else s,
        // No offset at all -> assume UTC, which is what the database stores.
        if (!s.contains('Z') && !Regex("""[+-]\d{2}(:\d{2})?$""").containsMatchIn(s)) "${s}Z" else s,
    )
    for (c in candidates.distinct()) {
        try {
            return kotlinx.datetime.Instant.parse(c).toEpochMilliseconds()
        } catch (_: Exception) { /* try the next shape */ }
    }
    return null
}

/** Format a duration in milliseconds as a compact string. Examples: "3s", "47s", "1m 12s", "2m 5s". */
fun formatDurationCompact(ms: Long): String {
    val totalSeconds = (ms / 1000).coerceAtLeast(0)
    val minutes = totalSeconds / 60
    val seconds = totalSeconds % 60
    return if (minutes > 0) "${minutes}m ${seconds}s" else "${seconds}s"
}

/** Compact relative time like "2m ago", "5h ago", "3d ago" from an ISO-8601 timestamp (e.g. a
 *  comment's created_at/edited_at). Returns "" on unparseable input so callers can simply omit
 *  the timestamp rather than showing junk. */
fun formatRelativeTime(iso: String, nowMillis: Long): String {
    val thenMillis = try {
        kotlinx.datetime.Instant.parse(iso).toEpochMilliseconds()
    } catch (_: Exception) {
        return ""
    }
    val diffSeconds = ((nowMillis - thenMillis) / 1000).coerceAtLeast(0)
    return when {
        diffSeconds < 60 -> "just now"
        diffSeconds < 3600 -> "${diffSeconds / 60}m ago"
        diffSeconds < 86400 -> "${diffSeconds / 3600}h ago"
        diffSeconds < 30L * 86400 -> "${diffSeconds / 86400}d ago"
        diffSeconds < 365L * 86400 -> "${diffSeconds / (30L * 86400)}mo ago"
        else -> "${diffSeconds / (365L * 86400)}y ago"
    }
}
